import { Decimal, Logger, NotificationService, useIntl } from '@capsicum/common';
import { ModalService, Snackbar } from '@capsicum/common-web';
import {
	useV2FTMigrationUpgradeScreen as useSharedV2FTMigrationUpgradeScreen,
	type UseV2FTMigrationUpgradeScreenProps
} from '@capsicum/features/nc-wallet/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useBrowserTransaction } from '@socios-blk/browser';
import { type Transaction, type TransactionFactory } from '@socios-blk/core';
import { useCallback, useEffect, useMemo } from 'react';

import { V2MigrationTxExecErrorModal } from '../../ui/v2-migration-tx-exec-error-modal/V2MigrationTxExecErrorModal';

export const useV2FTMigrationUpgradeScreen = ({ changeScreen, tokens }: UseV2FTMigrationUpgradeScreenProps) => {
	const { $t } = useIntl();
	const { refetchNcWalletBalance } = useNCWBalance();

	const {
		estimateGasFee,
		gasFeeEstimate,
		sendTransaction: sendBrowserTransaction,
		TRANSACTIONS,
		userOpStatus
	} = useBrowserTransaction();

	const {
		altCurrencyFee,
		canEstimateGasFees,
		defaultCurrencyFee,
		hasErrored,
		isLoading,
		isNetworkFeeLoading,
		paymasterParams,
		sendAnalytics,
		setErrored,
		setGasEstimateLoading,
		setTransactionLoading,
		stakingSmartContractAddress,
		swapSmartContractAddress,
		syncMigrationTXHash
	} = useSharedV2FTMigrationUpgradeScreen({ gasFeeEstimate, tokens });

	const userOperations = useMemo(
		() =>
			!swapSmartContractAddress || !stakingSmartContractAddress
				? []
				: tokens.reduce<Array<ReturnType<TransactionFactory[keyof TransactionFactory]> | Transaction>>(
						(userOperations, token) => {
							// Swap Unstaked v1 FTs.
							if (new Decimal(token.usable).greaterThan(0)) {
								userOperations.push(
									TRANSACTIONS.APPROVE({
										contractAddress: token.smartContractAddress,
										spender: swapSmartContractAddress,
										value: token.usable
									}),

									TRANSACTIONS.SWAP_V2({
										oldFTAddress: token.smartContractAddress,
										oldFTAmount: token.usable,
										swapContractAddress: swapSmartContractAddress
									})
								);
							}

							const nonusableTokens = new Decimal(token.staked).plus(token.unstaked).toString();

							// Swap Staked & Non-Claimable & Claimable v1 FTs.
							if (new Decimal(nonusableTokens).greaterThan(0)) {
								userOperations.push(
									TRANSACTIONS.APPROVE({
										contractAddress: token.smartContractAddress,
										decimals: 0,
										spender: stakingSmartContractAddress,
										value: nonusableTokens
									}),

									TRANSACTIONS.APPROVE({
										contractAddress: token.v2SmartContractAddress,
										decimals: 18,
										spender: stakingSmartContractAddress,
										value: nonusableTokens
									}),

									TRANSACTIONS.SWAP_STAKED_TOKENS_V2({
										oldFTAddress: token.smartContractAddress,
										stakingContractAddress: stakingSmartContractAddress
									})
								);
							}

							return userOperations;
						},
						[]
					),
		[tokens, TRANSACTIONS, swapSmartContractAddress, stakingSmartContractAddress]
	);

	const showGasFeesError = useCallback(() => {
		setErrored(true);

		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('v2FTMigrationInitFlow.upgrade.notifications.estimateGasFeesError')}
					testID="v2FTMigrationInitFlow-estimateGasFees-error"
					variant="error"
				/>
			)
		});
	}, [$t, setErrored]);

	const showTransactionExecutionError = useCallback(() => {
		setErrored(true);

		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('v2FTMigrationInitFlow.upgrade.notifications.executeTransactionError')}
					testID="v2FTMigrationInitFlow-executeTransaction-error"
					variant="error"
				/>
			)
		});

		ModalService.addModal({
			onDismiss: () => changeScreen('MODAL'),
			renderComponent: ({ close }) => <V2MigrationTxExecErrorModal close={close} />
		});
	}, [$t, setErrored, changeScreen]);

	const showMigrationNotification = useCallback(() => {
		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('v2FTMigrationInitFlow.upgrade.notifications.migrationPending.title')}
					testID="v2FTMigrationInitFlow-upgradingFanTokens-notification"
					title={$t('v2FTMigrationInitFlow.upgrade.notifications.migrationPending.description')}
					variant="pending"
				/>
			)
		});
	}, [$t]);

	useEffect(() => {
		if (!canEstimateGasFees) return;

		if (userOperations.length === 0) {
			if (!swapSmartContractAddress || !stakingSmartContractAddress) {
				Logger.error(
					'V2Migration: ERROR Gas fee estimation stopped because no swap or/and staking smart contract address configured in NCW Balance Endpoint',
					{ stakingSmartContractAddress, swapSmartContractAddress }
				);
			} else {
				Logger.error('V2Migration: ERROR Gas fee estimation stopped because no user operations were available');
			}

			showGasFeesError();
			setGasEstimateLoading(false);

			return;
		}

		setGasEstimateLoading(true);

		estimateGasFee({
			onError: err => {
				Logger.error('V2Migration: ERROR while estimating gas fees', { err });
				showGasFeesError();
			},
			onSettled: () => {
				setGasEstimateLoading(false);
			},
			paymasterParams,
			transaction: userOperations
		});
	}, [
		userOperations,
		paymasterParams,
		showGasFeesError,
		canEstimateGasFees,
		setGasEstimateLoading,
		swapSmartContractAddress,
		stakingSmartContractAddress
		// estimateGasFee, // This is not memoized and causes the useEffect to re-render. I didn't fix it so we don't affect other places in the app.
	]);

	const sendTransaction = useCallback(async () => {
		try {
			sendAnalytics();
			setTransactionLoading(true);

			await sendBrowserTransaction({
				onError: err => {
					if (err.code === 'TRANSACTION_HASH_MALFORMED_OR_EMPTY') {
						Logger.error('V2Migration: ERROR transaction was submitted but we did not get a tx hash');
					} else if (err.code === 'SEND_USER_OP_FAILED' && err.type === 'UserCancelled') {
						Logger.error('V2Migration: ERROR user has cancelled transaction signing');
					} else {
						Logger.error('V2Migration: ERROR executing transaction', { err });
					}

					showTransactionExecutionError();
					setTransactionLoading(false);
				},
				onSuccess: ok => {
					const fn = async () => {
						try {
							if (ok.state !== 'SUBMITTED') {
								Logger.error('V2Migration: ERROR transaction was not submitted', { userOpStatus });

								showTransactionExecutionError();
								setTransactionLoading(false);

								return;
							}

							await syncMigrationTXHash(ok.transactionHash);
							await refetchNcWalletBalance();

							changeScreen('MODAL');
							showMigrationNotification();
						} catch (error) {
							Logger.error('V2Migration: ERROR analysing transaction', { error });

							showTransactionExecutionError();
							setTransactionLoading(false);
						}
					};

					fn();
				},
				paymasterParams,
				transaction: userOperations
			});
		} catch (error) {
			Logger.error('V2Migration: ERROR executing transaction', { error });
			showTransactionExecutionError();
			setTransactionLoading(false);
		}
	}, [
		changeScreen,
		sendAnalytics,
		userOperations,
		paymasterParams,
		syncMigrationTXHash,
		setTransactionLoading,
		sendBrowserTransaction,
		refetchNcWalletBalance,
		showMigrationNotification,
		showTransactionExecutionError
	]);

	return {
		altCurrencyFee,
		defaultCurrencyFee,
		hasErrored,
		isLoading,
		isNetworkFeeLoading,
		sendTransaction
	};
};
